import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminCancelAllocation } from '../api/allocationApi';
import { allocationKeys } from './allocationKeys';
import { allocationDetailsKeys } from './useAllocationDetails';
import { variationKeys } from './useVariation';
import { potKeys } from '../../finance/hooks/potKeys';
import { bookingDetailsKeys } from './useBookingDetails';

interface AdminCancelAllocationParams {
  allocationId: string;
  cancellationNotes?: string;
}

/**
 * Hook for admin cancelling an allocation.
 *
 * Errors are handled by the global QueryClient onError (shows snackbar).
 * Callers can override with mutate(data, { onSuccess, onError }).
 */
export function useAdminCancelAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      allocationId,
      cancellationNotes,
    }: AdminCancelAllocationParams) => {
      return await adminCancelAllocation(allocationId, cancellationNotes);
    },
    onSuccess: () => {
      // Invalidate all related queries - sites have allocation data
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      // Pots contain allocation references - use potKeys for correct cache invalidation
      queryClient.invalidateQueries({ queryKey: potKeys.all });
      // Allocation specific queries
      queryClient.invalidateQueries({ queryKey: allocationKeys.all });
      queryClient.invalidateQueries({ queryKey: allocationDetailsKeys.all });
      queryClient.invalidateQueries({ queryKey: bookingDetailsKeys.all });
      // Invalidate hierarchical table data (variations contain allocation counts)
      queryClient.invalidateQueries({ queryKey: variationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['labourItems'] });
    },
  });
}
